/**
 * ViewsProvider — estado compartido de "en qué vista se mira cada listado".
 *
 * Se monta una vez (en `App.jsx`, dentro del layout) y los listados consumen
 * `useListView(listKey, defaultView)`. Así el conmutador es el mismo en todas
 * las pantallas y la preferencia se guarda en el usuario, no en cada lista.
 *
 * Orden de prioridad al resolver la vista de un listado:
 *   1. lo que tenga guardado el usuario (servidor, y de respaldo la caché del
 *      navegador);
 *   2. la vista por defecto que declara el componente.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import {
  applyViewChange,
  cacheViews,
  isValidView,
  readStoredViews,
  resolveViews,
} from './viewPreferences';

const ViewsContext = createContext(null);

export function ViewsProvider({ companyId, children }) {
  const [views, setViews] = useState(() => readStoredViews());

  useEffect(() => {
    let cancelled = false;
    if (!companyId || companyId === 'all') return undefined;

    api.getViewPreferences(companyId)
      .then((data) => {
        if (!cancelled && data) {
          setViews((prev) => resolveViews(data.views, prev));
        }
      })
      .catch(() => {
        // Sin servidor: se sigue con la caché del navegador y la vista por defecto.
      });

    return () => { cancelled = true; };
  }, [companyId]);

  const rememberView = useCallback((listKey, view) => {
    const next = applyViewChange(views, listKey, view);
    cacheViews(next);
    setViews(next);
    if (companyId && companyId !== 'all') {
      // Se guarda también en el usuario. Si falla, la interfaz ya ha cambiado y
      // la caché local mantiene la elección en este navegador.
      api.saveViewPreference(companyId, listKey, view).catch(() => {});
    }
  }, [views, companyId]);

  const value = useMemo(() => ({ views, rememberView }), [views, rememberView]);

  return <ViewsContext.Provider value={value}>{children}</ViewsContext.Provider>;
}

/**
 * Vista activa de un listado.
 *
 * @param {string} listKey       clave estable del listado (`racks`, `locations`…)
 * @param {string} defaultView   vista declarada por el componente
 * @returns {{view: string, setView: Function, reset: Function, isDefault: boolean}}
 */
export function useListView(listKey, defaultView) {
  const ctx = useContext(ViewsContext);
  const stored = ctx?.views?.[listKey];
  const view = isValidView(stored) ? stored : defaultView;

  const setView = useCallback((next) => {
    // Elegir la vista por defecto equivale a no tener preferencia.
    ctx?.rememberView?.(listKey, next === defaultView ? null : next);
  }, [ctx, listKey, defaultView]);

  const reset = useCallback(() => {
    ctx?.rememberView?.(listKey, null);
  }, [ctx, listKey]);

  return { view, setView, reset, isDefault: view === defaultView };
}

export default ViewsContext;
